/* global globalThis */
/**
 * ManagePermissions
 *
 * Top-level screen to manage application permissions either by Roles or by Users.
 * Provides an edit mode that enables toggling permissions, tracks changes to build
 * an update payload, and persists via `updateRolePermissions`. Also shows a last
 * updated timestamp and a simple legend for R/W/D.
 */
import { useState } from "react";
import "./manage-permissions.css";
import Breadcrumb from "../common/Breadcrumb";
import RolesAndPermissions from "./roles-and-permissions/roles-and-permissions";
import RolesAndPermissionsdAddPopup from "./roles-and-permissions-add-popup/roles-and-permissions-add-popup";
import UserList from "./user-list/user-list";
import { usePermissions } from "../../redux/permissions";
import MODULES from "../../constants/modules";
import { addPermissions } from "../../api/roles";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";

dayjs.extend(utc);
dayjs.extend(timezone);

// Helper: Get access token from localStorage
const getAccessToken = () => {
  if (globalThis === undefined) return undefined;
  return (
    localStorage.getItem("access_token") ||
    localStorage.getItem("token") ||
    undefined
  );
};

// Helper: Format last updated timestamp
const formatLastUpdated = (lastUpdatedAt) => {
  if (!lastUpdatedAt) return "Last updated : ";
  const dateStr = dayjs
    .utc(lastUpdatedAt)
    .tz(dayjs.tz.guess())
    .format("MM/DD/YYYY");
  const timeStr = dayjs
    .utc(lastUpdatedAt)
    .tz(dayjs.tz.guess())
    .format("hh:mm A");
  return `Last updated : ${dateStr}   ${timeStr}`;
};

// Helper: Get tab button class name
const getTabButtonClassName = (tabName, activeTab) => {
  const baseClass = "manage-permissions-content-tab-btn";
  if (tabName === activeTab) {
    return `${baseClass} manage-permissions-content-tab-active`;
  }
  return baseClass;
};

// Helper: Get save button class name
const getSaveButtonClassName = (hasPermissions) => {
  const baseClass = "save-btn";
  if (hasPermissions) {
    return baseClass;
  }
  return `${baseClass} save-btn-disabled`;
};

// Helper: Get save button styles
const getSaveButtonStyles = (hasPermissions) => {
  if (hasPermissions) {
    return {
      cursor: "pointer",
      opacity: 1,
    };
  }
  return {
    cursor: "not-allowed",
    opacity: 0.6,
  };
};

// Helper: Get edit button styles
const getEditButtonStyles = (canWrite) => {
  if (canWrite) {
    return {
      opacity: 1,
      cursor: "pointer",
    };
  }
  return {
    opacity: 0.6,
    cursor: "not-allowed",
  };
};

// Helper: Get edit icon color
const getEditIconColor = (canWrite) => {
  if (canWrite) {
    return "#6E6D92";
  }
  return "#A0A0A0";
};

const ManagePermissions = () => {
  const [activeTab, setActiveTab] = useState("roles");
  const [editMode, setEditMode] = useState(false);
  const [isOpenAddPopup, setIsOpenAddPopup] = useState(false);
  const [updatedPermissions, setUpdatedPermissions] = useState([]);
  const [lastUpdatedAt, setLastUpdatedAt] = useState("");
  const [refreshKey, setRefreshKey] = useState(0);

  // Helper: Check if permissions update was successful
  const checkPermissionsUpdateSuccess = (res) => {
    if (res && !res.error) {
      console.log("Permissions updated successfully", res);
    } else {
      console.warn("Permissions update may have failed", res);
    }
  };

  const handleSaveChanges = async () => {
    if (!Array.isArray(updatedPermissions) || updatedPermissions.length === 0) {
      setEditMode(false);
      return;
    }
    const token = getAccessToken();
    const applicationId = 2; // per requirement /oauth/auth/addPermissions?applicationId=2
    try {
      const res = await addPermissions(
        applicationId,
        updatedPermissions,
        token,
      );
      checkPermissionsUpdateSuccess(res);
      setRefreshKey((k) => k + 1);
    } catch (error) {
      console.error("Failed to update permissions", error);
    } finally {
      setEditMode(false);
    }
  };
  const { canWrite } = usePermissions(MODULES.ROLE_MANAGEMENT);

  const handleCloseAddPopup = () => {
    setIsOpenAddPopup(false);
  };
  return (
    <>
      <div className="manage-permissions">
        <div className="manage-permissions-header">
          <Breadcrumb items={[{ label: "Role Management" }]} />
        </div>
        <div className="manage-permissions-content">
          <div className="manage-permissions-content-tab">
            <div className="manage-permissions-content-tab-row">
              <div className="manage-permissions-content-tab-container">
                <button
                  className={getTabButtonClassName("roles", activeTab)}
                  onClick={() => setActiveTab("roles")}
                >
                  Roles
                </button>
                <button
                  className={getTabButtonClassName("users", activeTab)}
                  onClick={() => setActiveTab("users")}
                >
                  User List
                </button>
              </div>

              {activeTab === "roles" && (
                <div className="manage-permissions-right">
                  <div className="manage-permissions-legend">
                    <span>
                      <strong>R</strong> - Read
                    </span>
                    <span className="legend-separator">|</span>
                    <span>
                      <strong>W</strong> - Write
                    </span>
                    <span className="legend-separator">|</span>
                    <span>
                      <strong>D</strong> - Delete
                    </span>
                  </div>
                  <div className="manage-permissions-last-updated">
                    {formatLastUpdated(lastUpdatedAt)}
                  </div>
                </div>
              )}
            </div>

            {activeTab === "roles" && (
              <div className="manage-permissions-actions">
                {editMode ? (
                  <>
                    <button
                      className="cancel-btn"
                      onClick={() => {
                        setEditMode(false);
                        setUpdatedPermissions([]);
                      }}
                    >
                      Cancel
                    </button>
                    <button
                      className={getSaveButtonClassName(
                        updatedPermissions.length > 0,
                      )}
                      onClick={handleSaveChanges}
                      disabled={updatedPermissions.length === 0}
                      style={getSaveButtonStyles(updatedPermissions.length > 0)}
                    >
                      Save Changes
                    </button>
                  </>
                ) : (
                  <button
                    className="edit-btn"
                    onClick={() => setEditMode(true)}
                    disabled={canWrite === false}
                    style={getEditButtonStyles(canWrite)}
                  >
                    Edit Permissions
                    <EditOutlinedIcon
                      sx={{
                        color: getEditIconColor(canWrite),
                        fontSize: "20px",
                      }}
                    />
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
        <div className="manage-permissions-table">
          {activeTab === "roles" && (
            <RolesAndPermissions
              editMode={editMode}
              canWrite={canWrite}
              setUpdatedPermissions={setUpdatedPermissions}
              setLastUpdatedAt={setLastUpdatedAt}
              refreshKey={refreshKey}
            />
          )}
          {activeTab === "users" && <UserList canWrite={canWrite} />}
        </div>
      </div>
      <RolesAndPermissionsdAddPopup
        isOpen={isOpenAddPopup}
        onClose={handleCloseAddPopup}
      />
    </>
  );
};

export default ManagePermissions;
